import { useState } from 'react';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import SupportTicketDialog from '../../support/components/SupportTicketDialog';
import useAppUser from '../../../hooks/useAppUser';
import { getHelpArticleById } from '../data/helpRegistry';
import { useHelp } from '../hooks/useHelp';

export default function HelpDrawer() {
  const { article, isOpen, closeHelp, openHelp } = useHelp();
  const { role } = useAppUser();
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);

  const sections = [
    { key: 'systemBehavior', title: 'Comportement du système', items: article.systemBehavior },
    { key: 'steps', title: 'Étapes conseillées', items: article.steps },
    { key: 'warnings', title: 'Points d’attention', items: article.warnings },
    { key: 'bestPractices', title: 'Bonnes pratiques', items: article.bestPractices },
  ].filter((section) => Array.isArray(section.items) && section.items.length > 0);

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={closeHelp}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: { xs: '92vw', sm: 420 },
            paddingBottom: 'env(safe-area-inset-bottom, 16px)',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              p: 2,
              pb: 1.5,
            }}
          >
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="center">
                <HelpOutlineIcon color="primary" fontSize="small" />
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  Aide contextuelle
                </Typography>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {article.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {article.summary}
              </Typography>
            </Stack>

            <IconButton aria-label="Fermer l’aide" onClick={closeHelp}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
            <Stack spacing={2}>
              {article.body.map((paragraph, index) => (
                <Typography key={`${article.id}-${index}`} variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
                  {paragraph}
                </Typography>
              ))}

              {sections.map((section) => (
                <Box key={section.key}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    {section.title}
                  </Typography>
                  <Stack component="ul" spacing={1} sx={{ pl: 2.5, m: 0 }}>
                    {section.items!.map((item, index) => (
                      <Box component="li" key={`${section.key}-${index}`}>
                        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}

              {article.relatedIds?.length ? (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                      Articles liés
                    </Typography>
                    <Stack spacing={0.5} alignItems="flex-start">
                      {article.relatedIds
                        .map((relatedId) => getHelpArticleById(relatedId))
                        .filter(Boolean)
                        .map((relatedArticle) => (
                          <Button
                            key={relatedArticle!.id}
                            variant="text"
                            size="small"
                            onClick={() => openHelp(relatedArticle!.id, relatedArticle!.category)}
                            sx={{
                              px: 0,
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            → {relatedArticle!.title}
                          </Button>
                        ))}
                    </Stack>
                  </Box>
                </>
              ) : null}

              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Besoin d’aide supplémentaire ?
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                  Si le problème persiste, vous pouvez nous transmettre un signalement depuis cette page.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<BugReportOutlinedIcon />}
                  onClick={() => setSupportDialogOpen(true)}
                >
                  Contacter l’équipe
                </Button>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      <SupportTicketDialog
        open={supportDialogOpen}
        onClose={() => setSupportDialogOpen(false)}
        variant={['Admin', 'Patron', 'Developpeur'].includes(String(role || '')) ? 'admin' : 'user'}
        role={role}
        moduleHint={article.category || article.id || 'help-drawer'}
      />
    </>
  );
}
